import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  FileText,
  Mail,
  Briefcase,
  Star,
  Calendar,
  Heart,
  Check,
  Plus,
} from 'lucide-react';
import { formsApi, appsApi, type Form } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { FORM_TEMPLATES, TEMPLATE_CATEGORIES, type FormTemplate } from '../features/form-builder/templates';

type LucideIcon = typeof Mail;

const ICON_MAP: Record<string, LucideIcon> = {
  Mail,
  Briefcase,
  Star,
  Calendar,
  Heart,
  FileText,
};

export function FormCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<'choose' | 'details'>('choose');
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    app_id: '',
  });

  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Form>) => formsApi.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      navigate(`/forms/${result.id}`);
    },
  });

  const handleSelectTemplate = (template: FormTemplate | null) => {
    setSelectedTemplate(template);
    if (template) {
      setFormData({
        ...formData,
        name: template.form.name,
        description: template.form.description || '',
      });
    } else {
      setFormData({
        ...formData,
        name: '',
        description: '',
      });
    }
    setStep('details');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.app_id) return;

    // Prepare form data with template layout if selected
    const layoutData = selectedTemplate
      ? JSON.stringify({
          sections: selectedTemplate.form.sections,
          settings: selectedTemplate.form.settings,
          theme: selectedTemplate.form.theme,
        })
      : JSON.stringify({
          sections: [{ id: crypto.randomUUID(), title: 'Section 1', fields: [] }],
          settings: {},
          theme: {},
        });

    createMutation.mutate({
      name: formData.name,
      description: formData.description,
      app_id: formData.app_id,
      slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
      layout: layoutData,
    });
  };

  const apps = appsData?.items || [];

  // Group templates by category
  const templatesByCategory = FORM_TEMPLATES.reduce<Record<string, FormTemplate[]>>(
    (acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc[template.category]?.push(template);
      return acc;
    },
    {}
  );

  const categoryOrder = ['contact', 'business', 'hr', 'survey', 'event', 'healthcare'];

  if (step === 'choose') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/forms')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
              Nouveau Formulaire
            </h2>
            <p className="text-sm text-secondary-500">
              Choisissez un modèle ou créez un formulaire vide
            </p>
          </div>
        </div>

        {/* Empty form option */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => handleSelectTemplate(null)}
            className={cn(
              'w-full text-left p-6 rounded-xl border-2 border-dashed transition-all',
              'border-secondary-300 hover:border-primary-400 hover:bg-primary-50/50',
              'dark:border-secondary-600 dark:hover:border-primary-500 dark:hover:bg-primary-900/10'
            )}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-secondary-100 dark:bg-secondary-800">
                <Plus className="h-7 w-7 text-secondary-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-secondary-900 dark:text-white">
                  Formulaire vide
                </h3>
                <p className="text-sm text-secondary-500 mt-1">
                  Commencez avec un formulaire vierge et ajoutez vos propres champs
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Templates by category */}
        <div className="space-y-8">
          <h3 className="text-md font-semibold text-secondary-900 dark:text-white">
            Ou choisissez un modèle pré-configuré
          </h3>

          {categoryOrder.map((category) => {
            const templates = templatesByCategory[category];
            if (!templates || templates.length === 0) return null;

            const categoryInfo = TEMPLATE_CATEGORIES[category as keyof typeof TEMPLATE_CATEGORIES];

            return (
              <div key={category}>
                <h4 className="text-sm font-semibold text-secondary-600 dark:text-secondary-400 uppercase tracking-wider mb-4">
                  {categoryInfo?.labelFr || category}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => {
                    const Icon = ICON_MAP[template.icon] || FileText;
                    const fieldCount = template.form.sections.reduce(
                      (acc, s) => acc + s.fields.length,
                      0
                    );

                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleSelectTemplate(template)}
                        className={cn(
                          'group text-left p-5 rounded-xl border-2 transition-all',
                          'border-secondary-200 hover:border-primary-400 hover:shadow-lg',
                          'dark:border-secondary-700 dark:hover:border-primary-500',
                          'bg-white dark:bg-secondary-800'
                        )}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0"
                            style={{ backgroundColor: `${template.form.theme.primaryColor}15` }}
                          >
                            <Icon
                              className="h-6 w-6"
                              style={{ color: template.form.theme.primaryColor }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-secondary-900 dark:text-white truncate">
                                {template.name}
                              </h4>
                            </div>
                            <p className="text-sm text-secondary-500 dark:text-secondary-400 line-clamp-2">
                              {template.description}
                            </p>

                            <div className="flex items-center gap-2 mt-3">
                              <Badge variant="secondary" className="text-[10px]">
                                {fieldCount} champs
                              </Badge>
                              <Badge variant="outline" className="text-[10px]">
                                {template.form.sections.length} section{template.form.sections.length > 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </div>

                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white">
                              <Check className="h-4 w-4" />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => setStep('choose')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            {selectedTemplate ? `Créer: ${selectedTemplate.name}` : 'Formulaire vide'}
          </h2>
          <p className="text-sm text-secondary-500">
            Configurez les informations de base
          </p>
        </div>
      </div>

      {/* Show selected template info */}
      {selectedTemplate && (
        <div
          className="rounded-xl border-2 p-4"
          style={{
            borderColor: `${selectedTemplate.form.theme.primaryColor}50`,
            backgroundColor: `${selectedTemplate.form.theme.primaryColor}05`,
          }}
        >
          <div className="flex items-center gap-4">
            {(() => {
              const Icon = ICON_MAP[selectedTemplate.icon] || FileText;
              return (
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-xl"
                  style={{ backgroundColor: `${selectedTemplate.form.theme.primaryColor}20` }}
                >
                  <Icon
                    className="h-6 w-6"
                    style={{ color: selectedTemplate.form.theme.primaryColor }}
                  />
                </div>
              );
            })()}
            <div>
              <h3 className="font-semibold text-secondary-900 dark:text-white">
                Modèle: {selectedTemplate.name}
              </h3>
              <p className="text-sm text-secondary-500">
                {selectedTemplate.form.sections.reduce((acc, s) => acc + s.fields.length, 0)} champs pré-configurés
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-secondary-200 bg-white p-6 dark:border-secondary-800 dark:bg-secondary-900">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du formulaire *</Label>
                <Input
                  id="name"
                  placeholder="Mon Formulaire"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Description du formulaire..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="app">Application *</Label>
                <Select
                  value={formData.app_id}
                  onValueChange={(value) => setFormData({ ...formData, app_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une application" />
                  </SelectTrigger>
                  <SelectContent>
                    {apps.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {apps.length === 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Vous devez d'abord créer une application.
                  </p>
                )}
              </div>
            </div>
          </div>

          {createMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">
                Erreur: {(createMutation.error as Error).message}
              </p>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep('choose')}
            >
              Retour
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || !formData.name.trim() || !formData.app_id}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le formulaire
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormCreatePage;
